/**
 * ESG-3959: read the MEASURED per-dimension Scope 1 breakdown off the by-dimension report.
 *
 * These replace four apportionment sites in `ScopeOneDetail.jsx` that computed per-category figures as
 * `share × org-wide total` — the org's fuel mix projected onto every site, with no marker telling the
 * reader so. The backend now sends the real split per dimension item (`scopeOneCategories`), so the
 * arithmetic here is grouping and summing only; nothing is estimated.
 *
 * Two inconsistencies the old share maths had, both gone by construction:
 *  - the annual table divided by the sum of the *shown* rows while the quarterly one divided by the
 *    org-wide `scope1Total`, so the two tables disagreed with each other;
 *  - the quarterly builders looked up `quarterlyCategoryTotals["refrigerants"]` while that map was
 *    keyed `"refrigerant"`, so the Refrigerants tab's quarterly table rendered all zeros.
 *
 * ESG-3960: everything here is keyed on the row's `dimensionItemId`, never its name. An org can hold
 * two ACTIVE dimension items sharing a name within one dimension (staging has two "Logistics"
 * subdivisions), and the backend orders rows by emissions descending — so a name-keyed Map kept
 * whichever twin had the LOWER total, and the row with real emissions rendered an empty breakdown.
 *
 * Kept in its own module (rather than as closures in the component) so it can be unit-tested — the
 * frontend has no component-rendering tests, only pure-function ones.
 */

/**
 * The tab keys used in the UI are not the backend's group names: the payload uses the singular
 * `refrigerant` while the tab is `refrigerants`. This is the single place that hop is expressed.
 */
export const SCOPE_ONE_GROUP_BY_TAB_KEY = {
  stationary: "STATIONARY",
  mobile: "MOBILE",
  refrigerants: "REFRIGERANT",
  // Accepted too, because the payload spelling appears in places where the tab key is expected.
  refrigerant: "REFRIGERANT",
};

const PERIOD_COUNT = 5; // Q1..Q4 then whole-year

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

/**
 * Stable key for a report row. Ids arrive as numbers but Map lookups must not depend on that, so
 * normalise to a string. Returns null for the synthetic "unattributed" footer row, which carries no id
 * and is never part of a per-item breakdown.
 */
export const rowKey = (row) => (row?.dimensionItemId == null ? null : String(row.dimensionItemId));

/**
 * Index a by-dimension report's rows as `dimensionItemId -> { GROUP -> category }`.
 *
 * An absent `scopeOneCategories` (the backend omits empty arrays, and rows are empty until the recalc
 * scheduler backfills) yields an empty index rather than throwing — callers then render "no breakdown"
 * instead of a fabricated one.
 */
export const indexScopeOneCategories = (content) => {
  const byItem = new Map();
  (content || []).forEach((row) => {
    const key = rowKey(row);
    if (key === null) return;
    const byGroup = {};
    (row.scopeOneCategories || []).forEach((category) => {
      if (category?.category) byGroup[category.category] = category;
    });
    byItem.set(key, byGroup);
  });
  return byItem;
};

const categoryFor = (index, itemId, tabKey) => {
  const group = SCOPE_ONE_GROUP_BY_TAB_KEY[tabKey];
  if (!group || itemId == null) return undefined;
  return index.get(String(itemId))?.[group];
};

/**
 * Annual per-category "By dimension" rows. `dimensionRows` supplies the order and membership already
 * decided by the page (level selection, filters); this only attaches each row's real figures for the
 * category in view. Each row must carry `id` (its `dimensionItemId`) alongside `name`.
 */
export const scopeOneCategoryRows = (dimensionRows, tabKey, index) =>
  (dimensionRows || []).map((row) => {
    const category = categoryFor(index, row.id, tabKey);
    return {
      id: row.id,
      name: row.name,
      value: num(category?.co2Emissions),
      consumption: num(category?.consumption),
      // Null, not zero: null means the fuel has no configured calorific value, which the table renders
      // as an em dash. Collapsing it to 0 would assert "no energy".
      kwh: category?.kwh == null ? null : num(category.kwh),
    };
  });

/**
 * The fuel rows shown when an annual dimension row is expanded. No grouping needed — the backend rows
 * are already one per (group × fuel × measure) for this dimension item.
 */
export const scopeOneFuelRows = (index, itemId, tabKey) => {
  const category = categoryFor(index, itemId, tabKey);
  return (category?.fuels || [])
    .map((fuel) => ({
      fuel: fuel.consumptionType,
      unit: fuel.measure || "",
      consumption: num(fuel.consumption),
      emissions: num(fuel.co2Emissions),
      kwh: fuel.kwh == null ? null : num(fuel.kwh),
    }))
    .filter((fuel) => fuel.emissions > 0 || fuel.consumption > 0)
    .sort((a, b) => b.emissions - a.emissions || a.fuel.localeCompare(b.fuel));
};

/**
 * The transpose of {@link scopeOneFuelRows}: for ONE fuel, that fuel's measured figures per dimension
 * item. Powers the per-fuel table's row expansion.
 *
 * ESG-3960: this replaces the last apportionment site in `ScopeOneDetail` — a fifth one ESG-3959 missed
 * because it ran fuel → dimensions rather than dimension → fuels. It computed each item's slice as
 * `fuel total × (item's share of the level's emissions)`, i.e. it asserted every site burned the fuel in
 * proportion to its overall footprint. Sites that burn none of it were shown a figure anyway.
 *
 * @param index         from {@link indexScopeOneCategories}
 * @param dimensionRows the level's rows as `{id, name}` (order and membership decided by the caller)
 * @param tabKey        the category in view
 * @param fuel          the fuel's display name, as it appears in `consumptionType`
 * @param unit          the fuel's measure; "" matches a row with no measure
 */
export const scopeOneDimensionRowsForFuel = (index, dimensionRows, tabKey, fuel, unit) =>
  (dimensionRows || [])
    .map((row) => {
      const match = (categoryFor(index, row.id, tabKey)?.fuels || []).find(
        (f) => f.consumptionType === fuel && (f.measure || "") === (unit || "")
      );
      if (!match) return null;
      return {
        id: row.id,
        name: row.name,
        consumption: num(match.consumption),
        emissions: num(match.co2Emissions),
        kwh: match.kwh == null ? null : num(match.kwh),
      };
    })
    .filter((row) => row && (row.emissions > 0 || row.consumption > 0))
    .sort((a, b) => b.emissions - a.emissions || (a.name || "").localeCompare(b.name || ""));

/**
 * Quarterly per-category rows: one entry per dimension item, each with its five periods and a
 * per-fuel roll-down.
 *
 * @param quarterlyContents five `content` arrays, indexed Q1..Q4 then whole-year.
 * @param items             the items to report as `{id, name}`, in display order (decided by the caller).
 */
export const scopeOneQuarterlyCategoryRows = (quarterlyContents, tabKey, items) => {
  const indexes = (quarterlyContents || []).map((content) => indexScopeOneCategories(content));
  return (items || []).map(({ id, name }) => {
    const periods = [];
    // Fuel rows are unioned across periods so a fuel present in only one quarter still gets a row,
    // with zeros elsewhere, rather than shifting the columns.
    const fuelMap = new Map();
    for (let pi = 0; pi < PERIOD_COUNT; pi += 1) {
      const category = indexes[pi] ? categoryFor(indexes[pi], id, tabKey) : undefined;
      periods.push({
        emissions: num(category?.co2Emissions),
        kwh: category?.kwh == null ? null : num(category.kwh),
      });
      (category?.fuels || []).forEach((fuel) => {
        const key = `${fuel.consumptionType}|${fuel.measure || ""}`;
        if (!fuelMap.has(key)) {
          fuelMap.set(key, {
            fuel: fuel.consumptionType,
            unit: fuel.measure || "",
            periods: Array.from({ length: PERIOD_COUNT }, () => ({ emissions: 0, kwh: null })),
          });
        }
        const slot = fuelMap.get(key).periods[pi];
        slot.emissions += num(fuel.co2Emissions);
        if (fuel.kwh != null) slot.kwh = (slot.kwh || 0) + num(fuel.kwh);
      });
    }
    const fuels = [...fuelMap.values()]
      .filter((f) => f.periods.some((p) => p.emissions > 0))
      .sort((a, b) => (b.periods[4]?.emissions || 0) - (a.periods[4]?.emissions || 0));
    return { id, name, periods, fuels };
  });
};

/**
 * Per-(item × category × period) emissions for the stacked bar chart's category filter.
 * `tabKeys` is the set of categories actually present on the page.
 */
export const scopeOneQuarterlyByCategory = (quarterlyContents, tabKeys, items) => {
  const indexes = (quarterlyContents || []).map((content) => indexScopeOneCategories(content));
  return (items || []).map(({ id, name }) => {
    const byCategory = {};
    (tabKeys || []).forEach((tabKey) => {
      byCategory[tabKey] = Array.from({ length: PERIOD_COUNT }, (_, pi) =>
        num(indexes[pi] ? categoryFor(indexes[pi], id, tabKey)?.co2Emissions : 0)
      );
    });
    return { id, name, byCategory };
  });
};
